import React, { useMemo, useState } from "react"
import { UIMatch, useLocation, useMatches } from "react-router-dom"
import { pagesUrl } from "@app/core/appConstants"
import { Children } from "@app/core/type/Children"
import { UrlStateEnum } from "./UrlStateEnum"

/** Width of the opened side bar, in pixels. */
export const APP_SIDE_BAR_OPEN_WIDTH = 240
/** Width of the closed side bar, which only shows its icons: theme.spacing(7) plus 1px, as in MUI's mini drawer. */
export const APP_SIDE_BAR_CLOSED_WIDTH = 57

/** What a route of the application can declare in its `handle`, for the layout. */
export interface AppRouteHandle {
  /** False on the pages that take the whole screen (a module's own screens, AVA). Left out: the side bar shows. */
  showAppSide?: boolean
}

/** The handle of the routes whose pages hide the side bar. */
export const withoutAppSideBar: AppRouteHandle = { showAppSide: false }

/** The deepest route that says whether the side bar shows decides; when no route says it, the side bar shows. */
export function isAppSideShown(matches: ReadonlyArray<Pick<UIMatch, "handle">>): boolean {
  const deciding = [...matches]
    .reverse()
    .map((match) => match.handle as AppRouteHandle | undefined)
    .find((handle) => handle?.showAppSide !== undefined)
  return deciding?.showAppSide ?? true
}

export const AppSideBarContext = React.createContext<AppSideBarStore>({} as AppSideBarStore)

export function getUrlState(path: string): UrlStateEnum | undefined {
  if (path.includes("/beem-shot")) {
    return UrlStateEnum.BEEM_SHOT
  } else if (path.includes("/rsee")) {
    return UrlStateEnum.BEEM_PILOT
  } else if (path.includes(pagesUrl.HOME)) {
    return UrlStateEnum.HOME_PAGE
  } else if (path.includes(pagesUrl.OPEE_ANALYSIS)) {
    return UrlStateEnum.OPEE_ANALYSIS
  } else if (path.includes(pagesUrl.GLOBAL_RSEE_DASHBOARD)) {
    return UrlStateEnum.GLOBAL_RSEE_DASHBOARD
  } else if (path.includes(pagesUrl.PROJECTS_PAGE) || path.includes("/project")) {
    return UrlStateEnum.PROJECTS
  } else if (path.includes(pagesUrl.MACRO_MAKER_PAGE)) {
    return UrlStateEnum.MACRO_MAKER
  } else if (path.includes(pagesUrl.MY_GAMMES_PAGE)) {
    return UrlStateEnum.MY_GAMMES
  } else if (path.includes(pagesUrl.INIES_CHANGES_PAGE)) {
    return UrlStateEnum.INIES_CHANGES
  } else if (path.includes(pagesUrl.BEEM_SHOT_HELP_CENTER)) {
    return UrlStateEnum.TUTORIELS
  } else {
    return undefined
  }
}

export function AppSideBarContextProvider({ children }: Readonly<Children>): React.JSX.Element {
  const location = useLocation()
  const matches = useMatches()

  const [openAppSide, setOpenAppSide] = useState<boolean>(false)

  const showAppSide = isAppSideShown(matches)
  const urlState = useMemo(() => getUrlState(location.pathname), [location.pathname])

  const AppSideBarStore: AppSideBarStore = useMemo(
    () => ({
      showAppSide,
      urlState,
      openAppSide,
      setOpenAppSide,
    }),
    [openAppSide, showAppSide, urlState]
  )

  return <AppSideBarContext.Provider value={AppSideBarStore}>{children}</AppSideBarContext.Provider>
}

export interface AppSideBarStore {
  /** Declared by the routes, see {@link AppRouteHandle}. */
  showAppSide: boolean
  urlState: UrlStateEnum | undefined
  openAppSide: boolean
  setOpenAppSide: React.Dispatch<React.SetStateAction<boolean>>
}
